import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useMemo, useRef } from 'react';
import ReactDatePicker from 'react-datepicker';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { z } from 'zod';
import type { DocumentType, OwnerTypeEnum, PaginatedDocumentTypeList } from '../../../js/api';
import { DocumentsService } from '../../../js/api';
import { handleApiError } from '../../../js/app/utils/errorhandling';
import { parseFormDate } from '../utils/date';
import { pickableUploadTypes } from '../utils/documentTypes';
import { describeDocumentVisibility } from '../utils/documentVisibility';
import { uploadDocument } from '../utils/uploadDocument';
import { useAudience } from '../utils/workflowAccess';
import { Button } from './button';
import { Checkbox, CheckboxField } from './checkbox';
import { Dialog, DialogActions, DialogBody, DialogTitle } from './dialog';
import { DropdownSelect, type CodeDefinitionOption } from './dropdown-select';
import { FieldValidationErrorMessage } from './field-validation-error-message';
import { Field, Fieldset, Label } from './fieldset';
import { Input } from './input';
import 'react-datepicker/dist/react-datepicker.css';

// Shared query key for document types. Consumers that also query document
// types (e.g. gating an upload button on whether any uploadable types exist)
// can import this constant to share the React Query cache.
export const DOCUMENT_TYPES_QUERY_KEY = 'documentTypes';

type DocumentUploadDialogProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /**
   * Who owns the uploaded document. Also used to filter the document-type
   * picker to types this audience is allowed to upload.
   */
  ownerType: OwnerTypeEnum;
  ownerId: string;
  /** The context type the document is attached to (e.g. "connections.connectedfacility") */
  contextType: string;
  /** The context entity ID the document is attached to */
  contextId: string;
  /**
   * Optional related provider for HCO-owned documents that pertain to a
   * specific provider (e.g. a facility confirmation letter).
   */
  relatedProviderId?: string;
  /**
   * Pin the document type (by slug) and hide the picker — for in-context uploads where
   * the place in the UI already says what the document is (e.g. the CV on the provider's General tab).
   */
  fixedDocumentTypeSlug?: string;
};

const uploadFormSchema = z.object({
  document_type_slug: z.string().min(1, 'Document type is required'),
  label: z.string().optional(),
  file: z.instanceof(File, { message: 'A file is required' }),
  issued_on: z.date().nullable().optional(),
  expires_on: z.date().nullable().optional(),
  // Applies only to signature-requiring types: checked starts e-sign, unchecked
  // records an already-signed manual upload.
  request_esignature: z.boolean().optional(),
});

type UploadFormData = z.infer<typeof uploadFormSchema>;

export function DocumentUploadDialog({
  open,
  onClose,
  onSuccess,
  ownerType,
  ownerId,
  contextType,
  contextId,
  relatedProviderId,
  fixedDocumentTypeSlug,
}: DocumentUploadDialogProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const audience = useAudience();

  const form = useForm<UploadFormData>({
    resolver: zodResolver(uploadFormSchema),
    mode: 'onChange',
    defaultValues: {
      document_type_slug: fixedDocumentTypeSlug ?? '',
      label: '',
      issued_on: null,
      expires_on: null,
      request_esignature: true,
    },
  });

  const selectedTypeSlug = form.watch('document_type_slug');

  // Fetch document types for this context. The backend scopes to types the
  // user's audience has any permission on; we filter to uploadable types
  // client-side from the audience arrays.
  const { data: allTypes, isLoading: isLoadingTypes } = useQuery({
    queryKey: [DOCUMENT_TYPES_QUERY_KEY, contextType, ownerType],
    queryFn: () =>
      DocumentsService.documentsTypesList({
        contextTypes: [contextType],
      }),
    enabled: open,
    select: (data: PaginatedDocumentTypeList) => data?.results ?? [],
  });

  const documentTypes = useMemo(
    () => pickableUploadTypes(allTypes ?? [], ownerType, fixedDocumentTypeSlug),
    [allTypes, ownerType, fixedDocumentTypeSlug]
  );

  const selectedType: DocumentType | undefined = useMemo(
    () => documentTypes?.find((dt) => dt.slug === selectedTypeSlug),
    [documentTypes, selectedTypeSlug]
  );

  const typeOptions: CodeDefinitionOption[] = useMemo(
    () =>
      (documentTypes ?? []).map((dt) => ({
        value: dt.slug,
        label: dt.title,
      })),
    [documentTypes]
  );

  const acceptAttr = useMemo(() => {
    const mimeTypes = selectedType?.allowed_mime_types;
    if (!mimeTypes || mimeTypes.length === 0) return undefined;
    return mimeTypes.join(',');
  }, [selectedType]);

  const isExpirable = !!selectedType?.is_expirable;

  // Who will be able to see the document, in the uploader's words — shown as soon
  // as a type is chosen (or pinned) so nobody uploads something to the wrong eyes.
  const visibility = useMemo(
    () => (selectedType ? describeDocumentVisibility(selectedType, audience) : null),
    [selectedType, audience]
  );

  const resetForm = () => {
    form.reset({
      document_type_slug: fixedDocumentTypeSlug ?? '',
      label: '',
      issued_on: null,
      expires_on: null,
      request_esignature: true,
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const uploadMutation = useMutation({
    mutationFn: (data: UploadFormData) =>
      // presigned request → PUT to S3 → confirm (shared with in-context forms)
      uploadDocument({
        file: data.file,
        documentTypeSlug: data.document_type_slug,
        ownerType,
        ownerId,
        contextType,
        contextId,
        relatedProviderId,
        label: data.label,
        issuedOn: data.issued_on ? parseFormDate(data.issued_on) : undefined,
        expiresOn: data.expires_on ? parseFormDate(data.expires_on) : undefined,
        requestEsignature: data.request_esignature ?? true,
      }),
    onSuccess: () => {
      toast.success('Document uploaded successfully');
      resetForm();
      onSuccess();
      onClose();
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Upload failed' });
    },
  });

  const onSubmit = form.handleSubmit((data) => {
    // Issue / expiration dates are only required for is_expirable document
    // types — the schema accepts them as optional and we enforce the "required
    // when the selected type is expirable" rule here, because the rule depends
    // on external state (the selected DocumentType) that the schema can't see.
    if (isExpirable) {
      let hasError = false;
      if (!data.issued_on) {
        form.setError('issued_on', { message: 'Issue date is required' });
        hasError = true;
      }
      if (!data.expires_on) {
        form.setError('expires_on', { message: 'Expiration date is required' });
        hasError = true;
      }
      if (data.issued_on && data.expires_on && data.expires_on <= data.issued_on) {
        form.setError('expires_on', { message: 'Expiration date must be after the issue date' });
        hasError = true;
      }
      if (hasError) return;
    }
    uploadMutation.mutate(data);
  });

  return (
    <Dialog open={open} onClose={handleClose} size="xl">
      <DialogTitle className="tw:!text-2xl tw:!text-brand-green-0 tw:!mt-0 tw:!pt-0">
        {fixedDocumentTypeSlug ? `Upload ${selectedType?.title ?? 'Document'}` : 'Upload Document'}
      </DialogTitle>
      <DialogBody>
        <form onSubmit={onSubmit}>
          <Fieldset>
            <div className="tw:grid tw:grid-cols-1 tw:gap-4">
              {fixedDocumentTypeSlug ? null : (
                <Field>
                  <Label>
                    Document Type <span className="tw:text-stoplight-danger">*</span>
                  </Label>
                  <DropdownSelect
                    allOptions={typeOptions}
                    value={selectedTypeSlug || null}
                    onChange={(option) => {
                      const next = Array.isArray(option) ? (option[0] ?? null) : option;
                      form.setValue('document_type_slug', next ? next.value : '', {
                        shouldValidate: true,
                      });
                      // Reset file when type changes so accept attr is re-evaluated
                      form.setValue('file', undefined as unknown as File, {
                        shouldValidate: true,
                      });
                      // Clear dates when type changes — a non-expirable type
                      // shouldn't carry stale dates from a previous selection.
                      form.setValue('issued_on', null);
                      form.setValue('expires_on', null);
                      form.clearErrors(['issued_on', 'expires_on']);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    isMulti={false}
                    placeholder={
                      isLoadingTypes ? 'Loading document types...' : 'Select a document type'
                    }
                    isDisabled={isLoadingTypes}
                  />
                  {form.formState.errors.document_type_slug && (
                    <FieldValidationErrorMessage
                      message={form.formState.errors.document_type_slug.message}
                    />
                  )}
                </Field>
              )}
              {visibility && (
                <div
                  role="note"
                  data-testid="document-visibility-alert"
                  className="tw:rounded-lg tw:border tw:border-amber-200 tw:bg-amber-50 tw:px-4 tw:py-3 tw:text-sm tw:text-amber-800"
                >
                  <div className="tw:flex tw:items-start tw:gap-2">
                    <i className="fas fa-fw fa-eye tw:mt-0.5" aria-hidden="true" />
                    <div>
                      <p className="tw:!mb-0 tw:font-semibold">Who can see this document</p>
                      <p className="tw:!mb-0">{visibility.sentence}</p>
                    </div>
                  </div>
                </div>
              )}
              <Field>
                <Label>Name (optional)</Label>
                <Input
                  type="text"
                  placeholder="Defaults to filename if ommitted"
                  disabled={uploadMutation.isPending}
                  {...form.register('label')}
                />
                {form.formState.errors.label && (
                  <FieldValidationErrorMessage message={form.formState.errors.label.message} />
                )}
              </Field>
              {isExpirable && (
                <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4">
                  <Field>
                    <Label>
                      Issue Date <span className="tw:text-stoplight-danger">*</span>
                    </Label>
                    <Controller
                      name="issued_on"
                      control={form.control}
                      render={({ field }) => (
                        <ReactDatePicker
                          className="datepicker-input"
                          wrapperClassName="datepicker-wrapper"
                          selected={field.value ?? null}
                          onChange={(date) => field.onChange(date)}
                          placeholderText="Select issue date"
                          disabled={uploadMutation.isPending}
                          dateFormat="MM/dd/yyyy"
                        />
                      )}
                    />
                    {form.formState.errors.issued_on && (
                      <FieldValidationErrorMessage
                        message={form.formState.errors.issued_on.message}
                      />
                    )}
                  </Field>
                  <Field>
                    <Label>
                      Expiration Date <span className="tw:text-stoplight-danger">*</span>
                    </Label>
                    <Controller
                      name="expires_on"
                      control={form.control}
                      render={({ field }) => (
                        <ReactDatePicker
                          className="datepicker-input"
                          wrapperClassName="datepicker-wrapper"
                          selected={field.value ?? null}
                          onChange={(date) => field.onChange(date)}
                          placeholderText="Select expiration date"
                          disabled={uploadMutation.isPending}
                          dateFormat="MM/dd/yyyy"
                        />
                      )}
                    />
                    {form.formState.errors.expires_on && (
                      <FieldValidationErrorMessage
                        message={form.formState.errors.expires_on.message}
                      />
                    )}
                  </Field>
                </div>
              )}
              <Field>
                <Label>
                  File <span className="tw:text-stoplight-danger">*</span>
                </Label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={acceptAttr}
                  disabled={!selectedTypeSlug || uploadMutation.isPending}
                  onChange={(e) => {
                    const next = e.target.files?.[0];
                    if (next) {
                      form.setValue('file', next, { shouldValidate: true });
                    } else {
                      form.setValue('file', undefined as unknown as File, {
                        shouldValidate: true,
                      });
                    }
                  }}
                  className="tw:block tw:w-full tw:text-sm tw:text-zinc-700 tw:file:mr-3 tw:file:rounded-md tw:file:border-0 tw:file:bg-brand-green-2 tw:file:px-3 tw:file:py-1.5 tw:file:text-sm tw:file:font-medium tw:file:text-brand-green-0 tw:hover:file:bg-brand-green-1 tw:hover:file:text-white tw:disabled:opacity-50 tw:disabled:cursor-not-allowed"
                />
                {form.formState.errors.file && (
                  <FieldValidationErrorMessage message={form.formState.errors.file.message} />
                )}
                {selectedType?.max_upload_size_mb && (
                  <p className="tw:!mt-1 tw:text-xs tw:text-zinc-500">
                    Maximum file size: {selectedType.max_upload_size_mb} MB
                  </p>
                )}
              </Field>
              {selectedType?.require_signature && (
                <Field>
                  <Controller
                    name="request_esignature"
                    control={form.control}
                    render={({ field }) => (
                      <CheckboxField>
                        <Checkbox
                          color="brandgreen1"
                          checked={field.value ?? true}
                          onChange={field.onChange}
                          disabled={uploadMutation.isPending}
                          data-testid="request-esignature-checkbox"
                        />
                        <Label>Request E-Signature</Label>
                      </CheckboxField>
                    )}
                  />
                  <p className="tw:!mt-1 tw:text-xs tw:text-zinc-500">
                    Leave checked to start the e-signature process. Uncheck if this document has
                    already been signed off-platform.
                  </p>
                </Field>
              )}
            </div>
          </Fieldset>
        </form>
      </DialogBody>
      <DialogActions>
        <Button color="light" onClick={handleClose} disabled={uploadMutation.isPending}>
          Cancel
        </Button>
        <Button
          color="brandgreen0"
          onClick={onSubmit}
          disabled={!form.formState.isValid || uploadMutation.isPending}
          data-testid="document-upload-submit"
        >
          {uploadMutation.isPending ? 'Uploading...' : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
